/** Largest `maxVisibleDots` the carousel honors; larger values clamp to it. */
export const MAX_VISIBLE_DOTS = 5;

export type VisibleDotWindow = {
  /** Absolute snap indices for the dots that should render. */
  visibleIndices: number[];
  /** Index of the active snap within `visibleIndices` (0-based). */
  activeInWindow: number;
  /** Whether a valid `maxVisibleDots` was set, even if every snap fits. */
  capped: boolean;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Picks which pagination dots to show when an author caps the track length.
 *
 * Unset / non-finite / `< 1` → one dot per snap (today's behavior).
 * Values above {@link MAX_VISIBLE_DOTS} clamp to it.
 * When `snapCount <= max`, every snap is shown.
 * Otherwise returns an edge-clamped window of `max` absolute indices that
 * contains `selectedIndex`, keeping the active near the center in the middle.
 */
export function resolveVisibleDotWindow(
  snapCount: number,
  selectedIndex: number,
  maxVisibleDots?: number,
): VisibleDotWindow {
  const safeCount = Math.max(0, Math.floor(snapCount));
  const fullIndices = Array.from({ length: safeCount }, (_, index) => index);
  const clampedSelected =
    safeCount === 0 ? 0 : clamp(Math.floor(selectedIndex), 0, safeCount - 1);

  if (
    maxVisibleDots === undefined ||
    !Number.isFinite(maxVisibleDots) ||
    maxVisibleDots < 1
  ) {
    return {
      visibleIndices: fullIndices,
      activeInWindow: clampedSelected,
      capped: false,
    };
  }

  const max = Math.min(MAX_VISIBLE_DOTS, Math.floor(maxVisibleDots));
  if (safeCount <= max) {
    return {
      visibleIndices: fullIndices,
      activeInWindow: clampedSelected,
      capped: true,
    };
  }

  const half = Math.floor((max - 1) / 2);
  const start = clamp(clampedSelected - half, 0, safeCount - max);
  const visibleIndices = Array.from(
    { length: max },
    (_, index) => start + index,
  );

  return {
    visibleIndices,
    activeInWindow: clampedSelected - start,
    capped: true,
  };
}
